import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useTemplates } from '../hooks/useTemplates';
import { useComponents } from '../hooks/useComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Upload,
  Download,
  Loader2,
  Trash2,
  Plus,
  Package,
  Cog,
  FileDown,
  FileUp,
  X,
  Sliders,
  Save,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BEHAVIOR_CATEGORIES } from '../utils/behaviorsSystem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'templates', label: 'templates', icon: Package },
  { id: 'behaviors', label: 'behaviors', icon: Sliders },
  { id: 'components', label: 'components', icon: Cog },
  { id: 'import_export', label: 'import_export', icon: FileDown },
];

const AdminPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { templates, loading: templatesLoading, importBlockbench, deleteTemplate, fetchTemplates } = useTemplates();
  const { components, loading: componentsLoading, fetchComponents } = useComponents();

  const [activeTab, setActiveTab] = useState('templates');
  const [showAddComponent, setShowAddComponent] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-900"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{t('admin.title')}</h1>
              <p className="text-xs text-gray-500">{t('admin.subtitle')}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-1">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-600 border-transparent hover:text-gray-900'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {t(`admin.tabs.${tab.label}`)}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {activeTab === 'templates' && (
          <TemplatesTab
            templates={templates}
            loading={templatesLoading}
            onDelete={deleteTemplate}
            onImport={importBlockbench}
            onRefresh={fetchTemplates}
            t={t}
          />
        )}

        {activeTab === 'behaviors' && (
          <BehaviorConfigTab
            templates={templates}
            loading={templatesLoading}
            onRefresh={fetchTemplates}
          />
        )}

        {activeTab === 'components' && (
          <ComponentsTab
            components={components}
            loading={componentsLoading}
            onAdd={() => setShowAddComponent(true)}
            onRefresh={fetchComponents}
            t={t}
          />
        )}

        {activeTab === 'import_export' && (
          <ImportExportTab
            onRefreshComponents={fetchComponents}
            onRefreshTemplates={fetchTemplates}
            t={t}
          />
        )}
      </main>

      {/* Add Component Dialog */}
      <AddComponentDialog
        open={showAddComponent}
        onClose={() => setShowAddComponent(false)}
        onSuccess={() => {
          setShowAddComponent(false);
          fetchComponents();
        }}
        t={t}
      />
    </div>
  );
};

// ============================================
// TEMPLATES TAB
// ============================================
const TemplatesTab = ({ templates, loading, onDelete, onImport, onRefresh, t }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importName, setImportName] = useState('');
  const [importing, setImporting] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImportName(file.name.replace('.bbmodel', '').replace('.json', ''));
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !importName.trim()) {
      toast.error('Selecciona archivo y nombre');
      return;
    }
    setImporting(true);
    try {
      await onImport(selectedFile, importName, 'custom');
      toast.success(t('admin.template_imported'));
      setSelectedFile(null);
      setImportName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      toast.error(t('common.error'), { description: error.message });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async (template) => {
    try {
      const response = await axios.get(`${API}/templates/${template.id}/export`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.name.replace(/ /g, '_')}.cyberon-template.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Plantilla exportada');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`¿Eliminar plantilla "${template.name}"?`)) return;
    try {
      await onDelete(template.id);
      toast.success(t('admin.template_deleted'));
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Import Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          {t('admin.import_blockbench')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".bbmodel,.json"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="border-gray-200 justify-start truncate"
          >
            <Upload className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">
              {selectedFile ? selectedFile.name : t('admin.select_file')}
            </span>
          </Button>
          <Input
            value={importName}
            onChange={(e) => setImportName(e.target.value)}
            placeholder="Nombre de la plantilla"
            className="border-gray-200"
          />
          <Button
            onClick={handleImport}
            disabled={importing || !selectedFile}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t('common.import')}
          </Button>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">
            Plantillas disponibles ({templates.length})
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : templates.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">
            Aún no hay plantillas. Importa desde Blockbench para empezar.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map((template) => (
              <div
                key={template.id}
                className="p-3 border border-gray-200 rounded-lg hover:border-blue-500 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{template.name}</h3>
                    <p className="text-xs text-gray-500 truncate">{template.identifier}</p>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <button
                      onClick={() => handleExport(template)}
                      className="p-1.5 hover:bg-gray-100 rounded"
                      title="Exportar"
                    >
                      <Download className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(template)}
                      className="p-1.5 hover:bg-red-50 rounded text-gray-600 hover:text-red-500"
                      title="Eliminar"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <span className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                  {template.category}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// COMPONENTS TAB
// ============================================
const ComponentsTab = ({ components, loading, onAdd, onRefresh, t }) => {
  const handleDelete = async (component) => {
    if (!window.confirm(`¿Eliminar componente "${component.id}"?`)) return;
    try {
      await axios.delete(`${API}/components/${encodeURIComponent(component.id)}`);
      toast.success(t('admin.component_deleted'));
      onRefresh();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const componentsByCategory = React.useMemo(() => {
    const groups = {};
    components.forEach((comp) => {
      if (!groups[comp.category]) groups[comp.category] = [];
      groups[comp.category].push(comp);
    });
    return groups;
  }, [components]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Componentes ({components.length})
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Componentes de Minecraft disponibles para usar en proyectos
            </p>
          </div>
          <Button
            size="sm"
            onClick={onAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="add-new-component"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            {t('admin.new_component')}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(componentsByCategory).map(([category, comps]) => (
              <div key={category}>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {t(`behaviors.categories.${category}`, category)}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {comps.map((comp) => (
                    <div
                      key={comp.id}
                      className="p-3 border border-gray-200 rounded flex items-center gap-3 hover:bg-gray-50"
                    >
                      <span className="text-lg flex-shrink-0">{comp.icon || '·'}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">{comp.name_es || comp.name_en}</div>
                        <div className="text-xs text-gray-500 truncate">{comp.id}</div>
                      </div>
                      <button
                        onClick={() => handleDelete(comp)}
                        className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 flex-shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// IMPORT/EXPORT TAB
// ============================================
const ImportExportTab = ({ onRefreshComponents, onRefreshTemplates, t }) => {
  const templateFileRef = useRef(null);
  const componentsFileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const handleExportComponents = async () => {
    try {
      const response = await axios.get(`${API}/components/export/all`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cyberon-components.json';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Componentes exportados');
    } catch (error) {
      toast.error('Error al exportar componentes');
    }
  };

  const handleImportComponents = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/components/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Importados: ${response.data.imported}, Omitidos: ${response.data.skipped}`);
      onRefreshComponents();
    } catch (error) {
      toast.error('Error al importar componentes');
    } finally {
      setImporting(false);
      if (componentsFileRef.current) componentsFileRef.current.value = '';
    }
  };

  const handleImportTemplate = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/templates/import-cyberon`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Plantilla importada');
      onRefreshTemplates();
    } catch (error) {
      toast.error('Error al importar plantilla');
    } finally {
      setImporting(false);
      if (templateFileRef.current) templateFileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Components */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Cog className="w-4 h-4" />
          Componentes
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={handleExportComponents}
            className="border-gray-200 justify-start"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {t('admin.export_all_components')}
          </Button>
          <div>
            <input
              ref={componentsFileRef}
              type="file"
              accept=".json"
              onChange={handleImportComponents}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => componentsFileRef.current?.click()}
              disabled={importing}
              className="w-full border-gray-200 justify-start"
            >
              <FileUp className="w-4 h-4 mr-2" />
              {t('admin.import_cyberon_components')}
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Exporta todos tus componentes personalizados en un archivo JSON para compartir con otros PCs.
          Al importar, los componentes existentes no se sobrescribirán.
        </p>
      </div>

      {/* Templates */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Package className="w-4 h-4" />
          Plantillas
        </h2>
        <div>
          <input
            ref={templateFileRef}
            type="file"
            accept=".json"
            onChange={handleImportTemplate}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => templateFileRef.current?.click()}
            disabled={importing}
            className="border-gray-200 justify-start"
          >
            <FileUp className="w-4 h-4 mr-2" />
            {t('admin.import_cyberon_template')}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Importa plantillas exportadas desde otro PC. Para exportar una plantilla específica,
          usa el botón de descarga en la pestaña "Plantillas".
        </p>
      </div>
    </div>
  );
};

// ============================================
// ADD COMPONENT DIALOG
// ============================================
const AddComponentDialog = ({ open, onClose, onSuccess, t }) => {
  const [formData, setFormData] = useState({
    id: '',
    name_es: '',
    name_ca: '',
    name_en: '',
    icon: '',
    category: 'stats',
    description_es: '',
  });
  const [parameters, setParameters] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setFormData({
      id: '',
      name_es: '',
      name_ca: '',
      name_en: '',
      icon: '',
      category: 'stats',
      description_es: '',
    });
    setParameters([]);
  };

  const handleAddParameter = () => {
    setParameters([
      ...parameters,
      {
        name: '',
        type: 'number',
        default: 0,
        label_es: '',
        label_ca: '',
        label_en: '',
      },
    ]);
  };

  const handleUpdateParameter = (index, field, value) => {
    const newParams = [...parameters];
    newParams[index] = { ...newParams[index], [field]: value };
    setParameters(newParams);
  };

  const handleRemoveParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData.id.trim() || !formData.name_es.trim()) {
      toast.error('ID y nombre son obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      const component = {
        ...formData,
        name_ca: formData.name_ca || formData.name_es,
        name_en: formData.name_en || formData.name_es,
        parameters: parameters.map((p) => ({
          ...p,
          label_ca: p.label_ca || p.label_es,
          label_en: p.label_en || p.label_es,
          default: p.type === 'boolean' ? p.default === true || p.default === 'true' : parseFloat(p.default) || p.default,
        })),
        dependencies: [],
        minecraft_template: {},
        is_active: true,
      };

      await axios.post(`${API}/components`, component);
      toast.success(t('admin.component_added'));
      resetForm();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('admin.new_component')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-medium text-gray-700">
              {t('admin.component_id')} *
            </Label>
            <Input
              value={formData.id}
              onChange={(e) => setFormData({ ...formData, id: e.target.value })}
              placeholder="minecraft:my_component"
              className="mt-1 border-gray-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-gray-700">Nombre (Español) *</Label>
              <Input
                value={formData.name_es}
                onChange={(e) => setFormData({ ...formData, name_es: e.target.value })}
                className="mt-1 border-gray-200"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-gray-700">Nombre (Català)</Label>
              <Input
                value={formData.name_ca}
                onChange={(e) => setFormData({ ...formData, name_ca: e.target.value })}
                className="mt-1 border-gray-200"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-gray-700">Icono</Label>
              <Input
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="Emoji o texto"
                className="mt-1 border-gray-200"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-gray-700">Categoría</Label>
              <Select
                value={formData.category}
                onValueChange={(val) => setFormData({ ...formData, category: val })}
              >
                <SelectTrigger className="mt-1 border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stats">Stats</SelectItem>
                  <SelectItem value="movement">Movement</SelectItem>
                  <SelectItem value="ai">AI</SelectItem>
                  <SelectItem value="combat">Combat</SelectItem>
                  <SelectItem value="visual">Visual</SelectItem>
                  <SelectItem value="physics">Physics</SelectItem>
                  <SelectItem value="identity">Identity</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium text-gray-700">Descripción</Label>
            <Input
              value={formData.description_es}
              onChange={(e) => setFormData({ ...formData, description_es: e.target.value })}
              className="mt-1 border-gray-200"
            />
          </div>

          {/* Parameters */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold text-gray-900">Parámetros</Label>
              <Button size="sm" variant="outline" onClick={handleAddParameter} className="h-7 text-xs">
                <Plus className="w-3 h-3 mr-1" />
                {t('admin.add_parameter')}
              </Button>
            </div>

            {parameters.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-4">
                Sin parámetros. Añade uno para permitir configuración.
              </p>
            ) : (
              <div className="space-y-2">
                {parameters.map((param, idx) => (
                  <div key={idx} className="p-3 border border-gray-200 rounded space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input
                          value={param.name}
                          onChange={(e) => handleUpdateParameter(idx, 'name', e.target.value)}
                          placeholder="Nombre técnico"
                          className="h-8 text-xs border-gray-200"
                        />
                        <Input
                          value={param.label_es}
                          onChange={(e) => handleUpdateParameter(idx, 'label_es', e.target.value)}
                          placeholder="Etiqueta visible"
                          className="h-8 text-xs border-gray-200"
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveParameter(idx)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Select
                        value={param.type}
                        onValueChange={(val) => handleUpdateParameter(idx, 'type', val)}
                      >
                        <SelectTrigger className="h-8 text-xs border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="number">Número</SelectItem>
                          <SelectItem value="boolean">Boolean</SelectItem>
                          <SelectItem value="select">Select</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={param.default}
                        onChange={(e) => handleUpdateParameter(idx, 'default', e.target.value)}
                        placeholder="Valor por defecto"
                        className="h-8 text-xs border-gray-200"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t('common.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// BEHAVIOR CONFIG TAB (Admin)
// ============================================
const BehaviorConfigTab = ({ templates, loading, onRefresh }) => {
  const [selectedId, setSelectedId] = React.useState('');
  const [config, setConfig] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  const selectedTemplate = React.useMemo(
    () => templates.find((t) => t.id === selectedId),
    [templates, selectedId]
  );

  // Cargar config al cambiar de plantilla
  React.useEffect(() => {
    if (selectedTemplate) {
      setConfig(selectedTemplate.behavior_config || {});
      setDirty(false);
    } else {
      setConfig({});
    }
  }, [selectedTemplate]);

  const updateProp = (propId, patch) => {
    setConfig((prev) => {
      const next = { ...prev, [propId]: { ...(prev[propId] || {}), ...patch } };
      return next;
    });
    setDirty(true);
  };

  const clearProp = (propId) => {
    setConfig((prev) => {
      const next = { ...prev };
      delete next[propId];
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      // Limpiar entradas vacías (sin ninguna propiedad definida)
      const cleaned = {};
      Object.entries(config).forEach(([k, v]) => {
        if (!v || typeof v !== 'object') return;
        const keys = Object.keys(v).filter((kk) => v[kk] !== undefined && v[kk] !== '');
        if (keys.length > 0) cleaned[k] = v;
      });
      await axios.put(`${API}/templates/${selectedId}/behavior-config`, {
        behavior_config: cleaned,
      });
      toast.success('Configuración guardada');
      setDirty(false);
      onRefresh && onRefresh();
    } catch (err) {
      console.error(err);
      toast.error('Error al guardar', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-behaviors-tab">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-1">
          Configuración de comportamientos por plantilla
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Define los rangos permitidos (mínimo, máximo, paso, valor por defecto) que
          los alumnos podrán usar para cada comportamiento cuando creen proyectos con
          esta plantilla. Deja un campo vacío para usar el valor global.
        </p>

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Label className="text-xs font-medium text-gray-700 mb-1 block">
              Plantilla
            </Label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="h-9 border-gray-200" data-testid="admin-select-template">
                <SelectValue placeholder={loading ? 'Cargando...' : 'Selecciona una plantilla'} />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} <span className="text-xs text-gray-400">· {t.category}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="pt-5">
            <Button
              onClick={handleSave}
              disabled={!selectedId || !dirty || saving}
              className="bg-blue-600 hover:bg-blue-700 text-white h-9"
              data-testid="admin-behaviors-save"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </div>
      </div>

      {selectedTemplate ? (
        <div className="space-y-4">
          {BEHAVIOR_CATEGORIES.map((cat) => (
            <div key={cat.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {cat.label_es}
              </h3>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide px-2">
                  <div className="col-span-4">Propiedad</div>
                  <div className="col-span-1 text-center">Activo</div>
                  <div className="col-span-2 text-center">Default</div>
                  <div className="col-span-2 text-center">Mín</div>
                  <div className="col-span-2 text-center">Máx</div>
                  <div className="col-span-1 text-center">Paso</div>
                </div>
                {cat.properties.map((prop) => {
                  const c = config[prop.id] || {};
                  const enabledDefault = c.enabled !== false;
                  const isNumber = prop.type === 'number';
                  return (
                    <div
                      key={prop.id}
                      className="grid grid-cols-12 gap-2 items-center px-2 py-2 rounded hover:bg-gray-50 border border-transparent hover:border-gray-100"
                      data-testid={`admin-prop-row-${prop.id}`}
                    >
                      <div className="col-span-4">
                        <div className="text-sm font-medium text-gray-900">{prop.label_es}</div>
                        <div className="text-[10px] text-gray-400 font-mono truncate">{prop.id} · {prop.type}</div>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <input
                          type="checkbox"
                          checked={enabledDefault}
                          onChange={(e) => updateProp(prop.id, { enabled: e.target.checked })}
                          className="w-4 h-4 accent-blue-600"
                          title="Disponible para los alumnos"
                          data-testid={`admin-enabled-${prop.id}`}
                        />
                      </div>
                      <div className="col-span-2">
                        {prop.type === 'boolean' ? (
                          <Select
                            value={String(c.default ?? prop.default)}
                            onValueChange={(v) => updateProp(prop.id, { default: v === 'true' })}
                          >
                            <SelectTrigger className="h-8 text-xs border-gray-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="true">Activo</SelectItem>
                              <SelectItem value="false">Inactivo</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : prop.type === 'select' ? (
                          <Select
                            value={String(c.default ?? prop.default)}
                            onValueChange={(v) => updateProp(prop.id, { default: v })}
                          >
                            <SelectTrigger className="h-8 text-xs border-gray-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {prop.options.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type="number"
                            step={prop.step || 1}
                            value={c.default ?? prop.default ?? ''}
                            onChange={(e) => updateProp(prop.id, { default: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                            className="h-8 text-xs border-gray-200 text-center font-mono"
                            data-testid={`admin-default-${prop.id}`}
                          />
                        )}
                      </div>
                      <div className="col-span-2">
                        {isNumber ? (
                          <Input
                            type="number"
                            step={prop.step || 1}
                            placeholder={String(prop.min ?? '')}
                            value={c.min ?? ''}
                            onChange={(e) => updateProp(prop.id, { min: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                            className="h-8 text-xs border-gray-200 text-center font-mono"
                            data-testid={`admin-min-${prop.id}`}
                          />
                        ) : (
                          <div className="text-center text-gray-300">—</div>
                        )}
                      </div>
                      <div className="col-span-2">
                        {isNumber ? (
                          <Input
                            type="number"
                            step={prop.step || 1}
                            placeholder={String(prop.max ?? '')}
                            value={c.max ?? ''}
                            onChange={(e) => updateProp(prop.id, { max: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                            className="h-8 text-xs border-gray-200 text-center font-mono"
                            data-testid={`admin-max-${prop.id}`}
                          />
                        ) : (
                          <div className="text-center text-gray-300">—</div>
                        )}
                      </div>
                      <div className="col-span-1 flex items-center gap-1">
                        {isNumber ? (
                          <Input
                            type="number"
                            step={prop.step || 1}
                            placeholder={String(prop.step ?? '')}
                            value={c.step ?? ''}
                            onChange={(e) => updateProp(prop.id, { step: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                            className="h-8 text-xs border-gray-200 text-center font-mono"
                            data-testid={`admin-step-${prop.id}`}
                          />
                        ) : (
                          <div className="w-full text-center text-gray-300">—</div>
                        )}
                        {Object.keys(c).length > 0 && (
                          <button
                            onClick={() => clearProp(prop.id)}
                            title="Restaurar valores globales para esta propiedad"
                            className="p-1 text-gray-400 hover:text-red-500 rounded"
                            data-testid={`admin-reset-${prop.id}`}
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border-2 border-dashed border-gray-200 rounded-lg py-14 flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
            <Sliders className="w-7 h-7 text-blue-400" strokeWidth={1.5} />
          </div>
          <p className="text-sm font-medium text-gray-700">Selecciona una plantilla</p>
          <p className="text-xs text-gray-500">Configura los rangos permitidos de cada comportamiento</p>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
